/**
 * The transition log and the audit table (docs/ux/matter-hub.md): every
 * hand-off as a row with time, from and to chips, the verb, who did it and as
 * whom, the reason and the flags. Newest first. Cards on the phone.
 */

import { EVENT_DISPLAY, type Status } from '@beam/core/status';
import { cn } from 'cn';
import { ArrowRightIcon } from 'lucide-react';
import type { AuditRow } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { FlagText, activeFlags, type FlagKind } from '@/components/matter/FlagText';
import { StatusChip } from '@/components/matter/StatusChip';
import { actingAsDisplay } from '@/app/session';
import { Skeleton } from '@/components/ui/skeleton';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ErrorPanel } from '@/components/shell/states';
import { dateTime, longDate } from '@/lib/format';
import { TRANSITION_KINDS } from './actions';

export function eventVerb(kind: string): string {
  if (kind === 'created') return 'Created';
  return (EVENT_DISPLAY as Record<string, { label: string }>)[kind]?.label ?? kind.replace(/_/g, ' ');
}

function By({ e }: { e: AuditRow }) {
  const acting = actingAsDisplay(e.actingAsName, e.actingAs);
  return (
    <span className="flex flex-col leading-tight">
      <span>{e.userName ?? e.userId}</span>
      {acting ? <span className="text-xs text-beam-text-muted">acting as {acting}</span> : null}
    </span>
  );
}

function Flags({ flags }: { flags?: AuditRow['flags'] }) {
  if (!flags) return null;
  const kinds = activeFlags(flags as Partial<Record<FlagKind, boolean>>);
  if (kinds.length === 0) return null;
  return <span className="flex flex-wrap gap-2">{kinds.map((k) => <FlagText key={k} kind={k} />)}</span>;
}

function FromTo({ e }: { e: AuditRow }) {
  if (e.to === undefined) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {e.from !== undefined ? <><StatusChip status={e.from as Status} size="sm" /><ArrowRightIcon aria-hidden="true" className="size-3 text-beam-text-muted" /><span className="sr-only">to</span></> : <span className="text-beam-text-muted">Created at</span>}
      <StatusChip status={e.to as Status} size="sm" />
    </span>
  );
}

export type LogProps = { matterRef: string; events: AuditRow[] | undefined; isPending: boolean; isError: boolean; onRetry: () => void };

export function TransitionLog({ matterRef, events, isPending, isError, onRetry }: LogProps) {
  const phone = useIsPhone();
  const rows = (events ?? []).filter((e) => TRANSITION_KINDS.has(e.event)).toReversed();
  return (
    <section aria-labelledby="log-heading" data-slot="transition-log" className="rounded-xl border border-beam-line bg-beam-paper">
      <header className="flex items-baseline justify-between px-4 pt-3 pb-2">
        <h2 id="log-heading" className="eyebrow text-beam-text-muted">Transition log</h2>
        <span className="text-xs text-beam-text-muted">Newest first</span>
      </header>
      {isError ? (
        <div className="p-4"><ErrorPanel message="The transition log could not be loaded." onRetry={onRetry} /></div>
      ) : isPending ? (
        <div className="flex flex-col gap-px px-4 pb-4" aria-hidden="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[46px] rounded-none" />)}</div>
      ) : rows.length === 0 ? (
        <p className="px-4 pb-4 text-beam-text-muted">No transitions recorded yet.</p>
      ) : phone ? (
        <ul className="flex flex-col divide-y divide-beam-line">
          {rows.map((e) => (
            <li key={e.id}>
              <article className="flex flex-col gap-1.5 px-4 py-3 text-[13px]">
                <div className="flex justify-between text-beam-text-muted"><time dateTime={e.at} title={longDate(e.at)}>{dateTime(e.at)}</time><By e={e} /></div>
                <FromTo e={e} />
                <span className="font-medium">{eventVerb(e.event)}</span>
                {e.reason ? <p className="text-beam-text-muted">{e.reason}</p> : null}
                <Flags flags={e.flags} />
              </article>
            </li>
          ))}
        </ul>
      ) : (
        <table className="w-full text-[13px] leading-[1.3]">
          <caption className="sr-only">Transition log for {matterRef}, newest first</caption>
          <TableHeader className="bg-beam-cream-deep">
            <TableRow className="border-beam-line hover:bg-beam-cream-deep">
              <TableHead scope="col" className="eyebrow h-9 w-[120px] px-4 text-beam-text-soft">When</TableHead>
              <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Move</TableHead>
              <TableHead scope="col" className="eyebrow h-9 w-[160px] px-4 text-beam-text-soft">Event</TableHead>
              <TableHead scope="col" className="eyebrow h-9 w-[160px] px-4 text-beam-text-soft">By</TableHead>
              <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Reason and flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.id} data-event={e.event} className="min-h-[46px] border-beam-line hover:bg-beam-cream-deep">
                <TableCell className="tabular px-4 py-2.5 align-top whitespace-nowrap"><time dateTime={e.at} title={longDate(e.at)}>{dateTime(e.at)}</time></TableCell>
                <TableCell className="px-4 py-2.5 align-top"><FromTo e={e} /></TableCell>
                <TableCell className="px-4 py-2.5 align-top font-medium">{eventVerb(e.event)}</TableCell>
                <TableCell className="px-4 py-2.5 align-top"><By e={e} /></TableCell>
                <TableCell className="px-4 py-2.5 align-top whitespace-normal">
                  {e.reason ? <p className="text-beam-text-muted">{e.reason}</p> : null}
                  <Flags flags={e.flags} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </table>
      )}
    </section>
  );
}

function details(e: AuditRow): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(e.details ?? {})) parts.push(`${k}: ${String(v)}`);
  for (const [k, v] of Object.entries(e.counts ?? {})) parts.push(`${k}: ${v}`);
  if (e.reason) parts.push(`reason: ${e.reason}`);
  return parts.join(' · ');
}

export function AuditTable({ matterRef, events, isPending, isError, onRetry }: LogProps) {
  const phone = useIsPhone();
  const rows = (events ?? []).toReversed();
  return (
    <section aria-labelledby="audit-heading" data-slot="audit-table" className="rounded-xl border border-beam-line bg-beam-paper">
      <header className="flex items-baseline justify-between px-4 pt-3 pb-2">
        <h2 id="audit-heading" className="eyebrow text-beam-text-muted">Audit</h2>
        <span className="text-xs text-beam-text-muted">Current cycle, newest first; identifiers only</span>
      </header>
      {isError ? (
        <div className="p-4"><ErrorPanel message="The audit could not be loaded." onRetry={onRetry} /></div>
      ) : isPending ? (
        <div className="flex flex-col gap-px px-4 pb-4" aria-hidden="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[46px] rounded-none" />)}</div>
      ) : rows.length === 0 ? (
        <p className="px-4 pb-4 text-beam-text-muted">No events recorded for this cycle.</p>
      ) : (
        <div className={cn(phone && 'relative overflow-x-auto')}>
          <table className="w-full min-w-[640px] text-[13px] leading-[1.3]">
            <caption className="sr-only">Audit events for {matterRef}, newest first</caption>
            <TableHeader className="bg-beam-cream-deep">
              <TableRow className="border-beam-line hover:bg-beam-cream-deep">
                <TableHead scope="col" className="eyebrow h-9 w-[120px] px-4 text-beam-text-soft">When</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[180px] px-4 text-beam-text-soft">Event</TableHead>
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Move</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[160px] px-4 text-beam-text-soft">By</TableHead>
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} data-event={e.event} className="border-beam-line hover:bg-beam-cream-deep">
                  <TableCell className="tabular px-4 py-2.5 align-top whitespace-nowrap"><time dateTime={e.at} title={longDate(e.at)}>{dateTime(e.at)}</time></TableCell>
                  <TableCell className="px-4 py-2.5 align-top font-mono text-xs">{e.event}</TableCell>
                  <TableCell className="px-4 py-2.5 align-top"><FromTo e={e} /></TableCell>
                  <TableCell className="px-4 py-2.5 align-top"><By e={e} /></TableCell>
                  <TableCell className="px-4 py-2.5 align-top whitespace-normal text-beam-text-muted">
                    <span className="break-words">{details(e)}</span>
                    <Flags flags={e.flags} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
      )}
    </section>
  );
}
